/**
 * "Pushed" only when a write landed (CU-869f03vek, Curro 2026-10-28).
 *
 * The engine stamps the event's `pushed_price` when it DECIDES, before any send, and the flush never rewrites it
 * (pms-adapter `engine.py:976`, `flush.py:585`); the server now returns that value as `decided_net` and fills
 * `pushed_price` only when the write log says a write landed. The screen follows the same rule in words: a row whose
 * send did not happen — not sent in any of its faces, refused, already set, outranked, superseded, late, simulated —
 * shows "the price we would push", never "pushed". The truth of what was pushed lives in the write log (`write_status`
 * and `sends[].amount`), not on the decision.
 *
 * Pure: no React. Alias-free (TL R4).
 */
import { sentStyleOf } from './sendStyle.js'
import { isNotSentState, notSentChipLabel } from './notSentReason.js'

/** The `write_status` values under which a price actually landed in the PMS: `ok`, and an `ok` bound by its cap. */
export const WRITTEN_STATUSES = ['ok', 'capped']

/**
 * Tri-state. `true`: a write landed. `false`: the server knows nothing was written, or the row is a dry run.
 * `null`: the send is unknown (no `write_status` on the row) — nothing is claimed either way.
 */
export function writtenToPms(row) {
	// Simulated first: a dry run never writes, whatever status its send carries (QA 2026-10-28, a cap-bound dry run).
	if (row?.simulated) return false
	const status = row?.write_status
	if (status) return WRITTEN_STATUSES.includes(status)
	return null
}

/** The detail's push block title: the push only when there was one. */
export const pushTitle = (row) => (writtenToPms(row) === true ? 'Final PMS push' : 'Price we would push')

/** The label of the block's single line (no per-pricelist writes): pushed, would push, or today's neutral words when unknown. */
export function pushLineLabel(row) {
	const written = writtenToPms(row)
	if (written === true) return 'Pushed to the PMS'
	if (written === false) return 'Would push to the PMS'
	return 'Price to set in the PMS'
}

/**
 * The note under an amount nothing was written for: the Resolution's word plus "nothing written" — e.g.
 * "sent through Rentcars — nothing written". `null` when written, unknown, or simulated (the dry-run note says it).
 */
export function unwrittenNote(row) {
	if (writtenToPms(row) !== false || row?.simulated) return null
	const status = row.write_status
	const word = isNotSentState(status) ? notSentChipLabel(row) : sentStyleOf(status).label
	return `${word} — nothing written`
}
